import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getCurrentChannel } from '../config';
import './EntrancePopup.scss';
import useCustomFetchApi from '../utils/customFetchApi';

// Props:
// - canClose: show X button and allow overlay click to close (default: true)
// - showLater: show "Maybe Later" button (default: true)
// - onPrimaryAction: if provided, used for Claim button instead of onClose
const EntrancePopup = ({
  isVisible,
  onClose,
  title,
  description,
  subDescription,
  canClose = true,
  showLater = true,
  onPrimaryAction,
}) => {
  const api = useCustomFetchApi();
  const [btnHtml, setBtnHtml] = useState('');
  const [btnLink, setBtnLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Resolve channel once per render safely
  const channel = useMemo(() => {
    try { return getCurrentChannel() || 'global'; } catch (_) { return 'global'; }
  }, []);
  const gateway = 'stripe'; // default gateway; adjust if you add others later

  useEffect(() => {
    let mounted = true;
    const fetchedKeysRef = EntrancePopup.__fetchedKeysRef || (EntrancePopup.__fetchedKeysRef = { set: new Set() });
    const fetchPaymentLink = async () => {
      if (!isVisible) return; // only fetch when visible
      setLoading(true);
      setError('');
      try {
        const key = `${channel}|${gateway}`;
        if (fetchedKeysRef.set.has(key) && (btnHtml || btnLink)) {
          // Already fetched for this channel+gateway during this session, skip
          return;
        }
        const qs = new URLSearchParams({ channel, gateway }).toString();
        const data = await api.get(`/payment_link?${qs}`, { showNotification: false });
        const payload = data?.data || data || {};
        if (!mounted) return;
        const code = (payload.button_code ?? '').toString();
        const link = (payload.payment_link ?? '').toString();
        setBtnHtml(code);
        setBtnLink(link);
        try { fetchedKeysRef.set.add(key); } catch (_) {}
      } catch (e) {
        if (!mounted) return;
        setError(e.message || 'Failed to load payment link');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchPaymentLink();
    return () => { mounted = false; };
    // Intentionally omit `api` from deps to avoid re-fetch on render (identity changes frequently)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, channel, gateway, btnHtml, btnLink]);

  if (!isVisible) return null;

  return (
    <div className="entrance-popup-overlay" onClick={canClose ? onClose : undefined}>
      <div className="entrance-popup" onClick={(e) => e.stopPropagation()}>
        {canClose && (
          <button className="entrance-popup-close" onClick={onClose}>
            ×
          </button>
        )}
        <div className="entrance-popup-content">
          <h2 className="entrance-popup-title">{title}</h2>
          <p className="entrance-popup-description">{description}</p>
          {subDescription && subDescription.trim() && (
            <p className="entrance-popup-sub-description">{subDescription}</p>
          )}
          <div className="entrance-popup-actions" style={{ justifyContent: 'center' }}>
            {/* Original popup CTAs kept for reuse
            <button
              className="entrance-popup-claim-btn"
              onClick={onPrimaryAction || onClose}
            >
              Claim My Spot {(() => { try { return getCurrentChannel() === 'malaysia' ? <span className="price-suffix">(RM 6.9)</span> : null; } catch (_) { return null; } })()}
            </button>
            */}

            {/* Dynamic payment button from public API */}
            {loading ? (
              <div className="loading-row">Loading…</div>
            ) : error ? (
              <div className="error-message" style={{marginBottom:12}}>{error}</div>
            ) : btnHtml ? (
              <div
                className="payment-button-slot"
                // Note: button_code comes from trusted admin backend
                dangerouslySetInnerHTML={{ __html: btnHtml }}
              />
            ) : btnLink ? (
              <a
                className="entrance-popup-claim-btn"
                href={btnLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Continue to Payment
              </a>
            ) : null}

            {showLater && (
              <button className="entrance-popup-later-btn" onClick={() => onClose && onClose('later')}>
                Maybe Later
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default EntrancePopup;
